import { useQuery, useReactiveVar } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { CurrentUserActiveFirmQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserActiveFirmResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import ContainerMaxWidthXl from '@deltek/specification-client/core/Surfaces/ContainerMaxWidthXl'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { navigateToExternalLink } from '@deltek/specification-client/core/Utilities/urlUtils'
import { CircularProgress } from '@material-ui/core'
import AppBar from '@material-ui/core/AppBar'
import Backdrop from '@material-ui/core/Backdrop'
import IconButton from '@material-ui/core/IconButton'
import { useTheme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import CloseIcon from '@material-ui/icons/Close'
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined'
import MapOutlinedIcon from '@material-ui/icons/MapOutlined'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { setShowBestPracticeSetting } from 'api/graphQL/reactiveVars/workspaceToggles'
import clsx from 'clsx'
import DelaHeaderHOC from 'components/AskDelaChat/DelaHeaderHOC'
import { processEnv } from 'components/configuration/environmentVariables'
import { baseProjectImageUrl } from 'pages/ProjectsPage/components/projectImageUtil'
import React from 'react'
import ReactResizeDetector from 'react-resize-detector'
import { NavLink, useLocation } from 'react-router-dom'
import DeltekIcon from 'resources/DeltekIcon'
import { SPECPOINT_LEARN_LINK } from 'shared/common/externalLinks'
import { HiHome } from 'shared/HarmonyIcon'
import { useHarmonyIconButtonStyles } from 'shared/hooks/useHarmonyIconButtonStyles'
import { HARMONY_PANEL_HEIGHT } from 'shared/hooks/useHarmonyPanelStyles'
import useIsHarmonyHeader from 'shared/hooks/useIsHarmonyHeader'
import { useAppHeaderStyles } from './AppHeader.styles'
import DefaultActionBar from './DefaultActionBar'
import FullNavBarMenu from './FullNavBarMenu'
import HiddenOnBreakpoint from './HiddenOnBreakpoint'
import LightBulbIcon from './LightBulbIcon'
import NavBarMenu from './NavBarMenu'
import { CLASSIC_PANEL_HEIGHT } from './ProjectPagePartialNavBarMenu'
import ResponsiveBreakpointContainer from './ResponsiveBreakpointContainer'
import SpecpointHeaderLogo from './SpecpointHeaderLogo'
import TemplateNavIcon from './TemplateNavIcon'

/** Debounces resize reports so an animating panel doesn't trigger a re-render every frame. */
const HEADER_RESIZE_DEBOUNCE_MS = 100

export interface AppHeaderProps {
    /**
     * Defines the content for the right-hand side of the nav bar.
     */
    rightSideContent?: React.ReactNode

    /**
     * Defines the content for the middle portion of the nav bar.
     */
    middleContent?: React.ReactNode | null

    /**
     * Shows/hides the action bar.
     */
    hideActionBar?: boolean

    /**
     * This component is used in place of the default action bar
     * if a non-null value is provided.
     */
    customActionBar?: React.ReactNode | null

    /**
     * Handler for when the height of the header changes,
     * for example, for responsive design reasons.
     *
     * This is primarily used to adjust the height of
     * the top-level content container.
     */
    onHeightChange?: (newHeight: number) => void

    /**
     * Fires immediately when the nav panel opens/closes with its current height (0 when
     * closed). Faster than waiting for the debounced onHeightChange.
     */
    onPanelOffsetChange?: (panelHeight: number) => void

    /**
     * Title text used by the default action bar.
     * If a customActionBar is used, this value is unused.
     */
    pageTitle?: string

    /**
     * Menu items for the help menu dropdown.
     * This is a copy for the responsive full
     * page menu.
     */
    helpMenuItems?: MenuItemConfig[]

    /**
     * Menu items for the user menu dropdown.
     * This is a copy for the responsive full
     * page menu.
     */
    userMenuItems?: MenuItemConfig[]

    customMenuItems?: MenuItemConfig[] | React.ReactNode | null

    /**
     * Panel shown under the header when the nav menu is open. It is mounted only while
     * the menu is open. If it's a single element, AppHeader also injects an `isOpen` prop.
     */
    customPartialNavBarMenuComponent?: React.ReactNode | null

    /**
     * If visible, true
     */
    headerVisible?: boolean

    /**
     * When true, hides AE-specific navigation buttons (Home/Maps/Studio) and the training lightbulb.
     * Use when embedding AppHeader in non-AE contexts (e.g. BPM).
     */
    hideNavigation?: boolean

    /**
     * When provided, the hamburger button is always visible (regardless of middleContent) and
     * calls this handler instead of opening the nav panel. Use in non-AE contexts to toggle a sidebar.
     */
    hamburgerOnClick?: () => void

    /**
     * Controls the `aria-expanded` state of the hamburger when `hamburgerOnClick` is provided.
     * In non-AE contexts the sidebar's open state lives outside AppHeader, so pass it here
     * to keep screen-reader state accurate.
     */
    hamburgerIsExpanded?: boolean
}

const AppHeader: React.FC<AppHeaderProps> = ({
    pageTitle = '',
    rightSideContent,
    middleContent = null,
    hideActionBar = true,
    customActionBar = null,
    onHeightChange,
    onPanelOffsetChange,
    helpMenuItems = [],
    userMenuItems = [],
    customMenuItems = null,
    customPartialNavBarMenuComponent = null,
    headerVisible = true,
    hideNavigation = false,
    hamburgerOnClick,
    hamburgerIsExpanded,
}) => {
    const { pathname } = useLocation()
    const isHarmonyHeader = useIsHarmonyHeader()

    // remove this when SMM is production ready
    const isSMMEnabled = processEnv.REACT_APP_ShowSMM === '1'

    const [isMenuOpen, setIsMenuOpen] = React.useState(false)
    const menuPanelRef = React.useRef<HTMLDivElement>(null)
    const [isStudioIconLoaded, setIsStudioIconLoaded] = React.useState(false)
    const theme = useTheme()
    const xsDown = useMediaQuery(theme.breakpoints.down('xs'))
    const [warningOpen, setWarningOpen] = React.useState(true)

    const { isSystemUser } = useReactiveVar(userInfo)
    const { setUserActiveFirm } = React.useContext(AppBarContext)
    const classes = useAppHeaderStyles({
        isStudioIconLoaded,
    })
    const harmonyIconClasses = useHarmonyIconButtonStyles()
    const isStudioOrWorkItemPage =
        pathname.startsWith('/master') || pathname.startsWith('/workflow')
    const isHomeActive = pathname.startsWith('/projects/home')
    const isMapsActive = pathname.startsWith('/dashboard')

    const showHeaderDelaIcon =
        (pathname.includes('/projects') || isStudioOrWorkItemPage) &&
        process.env.REACT_APP_ShowSpecpointCopilot === '1'

    /* Close the menu on page change
     */
    React.useEffect(() => {
        if (xsDown) {
            setIsMenuOpen(false)
        }
    }, [pathname, xsDown])

    // Extra height the open nav panel adds (0 when closed, absent, or on mobile).
    const activePanelHeight =
        !xsDown && !!customPartialNavBarMenuComponent && isMenuOpen
            ? isHarmonyHeader
                ? HARMONY_PANEL_HEIGHT
                : CLASSIC_PANEL_HEIGHT
            : 0

    // Tells the parent right away, instead of waiting on the debounced height measurement below.
    React.useEffect(() => {
        onPanelOffsetChange?.(activePanelHeight)
    }, [activePanelHeight, onPanelOffsetChange])

    // 1. Marks <body> with "harmonyHeaderActive" so CSS can style the vendor's portaled menus.
    // 2. Watches for the user dropdown popover to appear/disappear and marks the button with
    //    "data-harmony-open" so CSS can apply the active-pill style. This is needed because
    //    the vendor's own "activeDropdown" class is minified in production builds and can't be targeted by CSS.
    React.useEffect(() => {
        document.body.classList.toggle('harmonyHeaderActive', isHarmonyHeader)
        if (!isHarmonyHeader) return

        const button = document.querySelector<HTMLElement>(
            '[data-testid="globalUserDropdownButton"]'
        )
        const observer = button
            ? new MutationObserver(() =>
                  button.toggleAttribute(
                      'data-harmony-open',
                      !!document.querySelector(
                          '[data-testid="globalPopoverUserMenu"]'
                      )
                  )
              )
            : null
        observer?.observe(document.body, { childList: true, subtree: false })

        return () => {
            document.body.classList.remove('harmonyHeaderActive')
            observer?.disconnect()
            button?.removeAttribute('data-harmony-open')
        }
    }, [isHarmonyHeader])

    const closeWarningOverlay = () => {
        setWarningOpen(false)
    }

    // Subtract the panel height (already reported via onPanelOffsetChange) so this stays just the bar's height.
    const handleHeightChange = (_: number, height: number) => {
        onHeightChange?.(height - activePanelHeight)
    }

    const handleMenuOpenClose = () => {
        setIsMenuOpen(!isMenuOpen)
    }

    // Moves keyboard focus into the collapsed nav panel as soon as it opens, so keyboard
    // users land somewhere useful instead of the panel opening silently off-screen from
    // their focus. The panel is a disclosure, not a modal, so focus is not trapped inside
    // it — Tab can move focus out normally, matching the WAI-ARIA disclosure pattern.
    React.useEffect(() => {
        if (!isMenuOpen) {
            return
        }
        const firstFocusable = menuPanelRef.current?.querySelector<HTMLElement>(
            'a[href], button, [tabindex]'
        )
        firstFocusable?.focus()
    }, [isMenuOpen])

    // Escape closes the panel and returns focus to the toggle button that opened it,
    // rather than leaving focus stranded inside content that just disappeared.
    React.useEffect(() => {
        if (!isMenuOpen) {
            return
        }
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') {
                return
            }
            setIsMenuOpen(false)
            document
                .querySelector<HTMLElement>('[data-testid="navBarMenuButton"]')
                ?.focus()
        }
        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [isMenuOpen])

    useQuery<CurrentUserActiveFirmResponse>(CurrentUserActiveFirmQuery, {
        onCompleted: (data) => {
            setUserActiveFirm?.(data?.myActiveFirm)

            // PBI 2683633: BP on for every firm.
            setShowBestPracticeSetting()
        },
    })

    const isStudioVisible = React.useMemo(
        () => !isSystemUser || (isSystemUser && isSMMEnabled),
        [isSystemUser]
    )

    const handleStudioImageLoad = React.useCallback(
        () => setIsStudioIconLoaded(true),
        [isStudioIconLoaded]
    )

    const loader = React.useMemo(
        () => (
            <div
                className={classes.studioLoading}
                data-testid="studioIconloader"
            >
                <CircularProgress aria-label="Loading" size={20} />
            </div>
        ),
        []
    )

    const partialMenuPanel = isMenuOpen && (
        <div ref={menuPanelRef}>
            {React.isValidElement<{ isOpen?: boolean }>(
                customPartialNavBarMenuComponent
            )
                ? React.cloneElement(customPartialNavBarMenuComponent, {
                      isOpen: isMenuOpen,
                  })
                : customPartialNavBarMenuComponent}
        </div>
    )

    const navBarMenuToggle = (
        <NavBarMenu
            isActive={
                hamburgerOnClick ? hamburgerIsExpanded ?? false : isMenuOpen
            }
            onClick={hamburgerOnClick ?? handleMenuOpenClose}
            hideLabel={!!hamburgerOnClick}
            ariaControls={hamburgerOnClick ? undefined : 'appHeaderNavPanel'}
        />
    )

    return (
        <GridContainer
            data-testid="appHeader"
            direction="column"
            className={classes.appHeaderContainer}
        >
            {headerVisible && (
                <>
                    <ReactResizeDetector
                        handleHeight={true}
                        handleWidth={false}
                        onResize={handleHeightChange}
                        refreshMode="debounce"
                        refreshRate={HEADER_RESIZE_DEBOUNCE_MS}
                    />
                    <GridItem container>
                        <AppBar
                            position="relative"
                            aria-label="Main navigation"
                            className={clsx(
                                'appHeader',
                                classes.appBarContainer,
                                classes.fullHeight,
                                isHarmonyHeader && classes.appBarContainerLight
                            )}
                        >
                            <ContainerMaxWidthXl>
                                <GridContainer
                                    alignItems="flex-start"
                                    className={clsx(
                                        classes.appHeaderContentContainer,
                                        isHarmonyHeader &&
                                            classes.appHeaderContentContainerLight
                                    )}
                                    justifyContent="space-between"
                                >
                                    <div
                                        className={clsx(
                                            classes.appHeaderLeftContentContainer,
                                            isHarmonyHeader &&
                                                hamburgerOnClick &&
                                                classes.appHeaderLeftContainerHamburgerGap
                                        )}
                                    >
                                        {hamburgerOnClick && navBarMenuToggle}
                                        <GridContainer
                                            alignItems="center"
                                            spacing={1}
                                            className={clsx(
                                                classes.logoContainer,
                                                isHarmonyHeader &&
                                                    classes.logoContainerLight
                                            )}
                                        >
                                            {isHarmonyHeader ? (
                                                <SpecpointHeaderLogo
                                                    className={
                                                        classes.logoImgLight
                                                    }
                                                />
                                            ) : (
                                                <DeltekIcon
                                                    className={
                                                        classes.deltekIcon
                                                    }
                                                ></DeltekIcon>
                                            )}
                                        </GridContainer>
                                        {!hideNavigation &&
                                            !pathname.includes(
                                                '/contacts/details/'
                                            ) &&
                                            (isHarmonyHeader ? (
                                                <HiddenOnBreakpoint>
                                                    <div
                                                        className={
                                                            classes.leftClusterLight
                                                        }
                                                    >
                                                        <NavLink to="/projects">
                                                            <IconButton
                                                                data-testid="buttonHome"
                                                                aria-label="Home Button"
                                                                aria-current={
                                                                    isHomeActive
                                                                        ? 'page'
                                                                        : undefined
                                                                }
                                                                disableRipple
                                                                disableFocusRipple
                                                                disableTouchRipple
                                                                className={clsx(
                                                                    harmonyIconClasses.iconButton,
                                                                    harmonyIconClasses.focusRing,
                                                                    isHomeActive &&
                                                                        harmonyIconClasses.iconButtonActive
                                                                )}
                                                            >
                                                                <HiHome
                                                                    className={
                                                                        harmonyIconClasses.icon
                                                                    }
                                                                    strokeWidth={
                                                                        1.6
                                                                    }
                                                                />
                                                            </IconButton>
                                                        </NavLink>
                                                        {isStudioVisible && (
                                                            <NavLink to="/master">
                                                                <IconButton
                                                                    data-testid="buttonStudio"
                                                                    aria-label="Studio Button"
                                                                    aria-current={
                                                                        isStudioOrWorkItemPage
                                                                            ? 'page'
                                                                            : undefined
                                                                    }
                                                                    disableRipple
                                                                    disableFocusRipple
                                                                    disableTouchRipple
                                                                    className={clsx(
                                                                        harmonyIconClasses.iconButton,
                                                                        harmonyIconClasses.focusRing,
                                                                        isStudioOrWorkItemPage &&
                                                                            harmonyIconClasses.iconButtonActive
                                                                    )}
                                                                >
                                                                    <TemplateNavIcon
                                                                        className={
                                                                            harmonyIconClasses.icon
                                                                        }
                                                                    />
                                                                </IconButton>
                                                            </NavLink>
                                                        )}
                                                    </div>
                                                </HiddenOnBreakpoint>
                                            ) : (
                                                <HiddenOnBreakpoint>
                                                    <div>
                                                        <NavLink to="/projects">
                                                            <IconButton
                                                                data-testid="buttonHome"
                                                                aria-label="Home Button"
                                                                aria-current={
                                                                    isHomeActive
                                                                        ? 'page'
                                                                        : undefined
                                                                }
                                                                disableRipple
                                                                disableFocusRipple
                                                                disableTouchRipple
                                                                className={clsx(
                                                                    classes.iconButton,
                                                                    classes.hasDivider,
                                                                    classes.whiteFont,
                                                                    classes.reset,
                                                                    isHomeActive &&
                                                                        classes.iconButtonActive
                                                                )}
                                                            >
                                                                <HomeOutlinedIcon />
                                                            </IconButton>
                                                        </NavLink>
                                                    </div>
                                                    <div>
                                                        <NavLink to="/dashboard">
                                                            <IconButton
                                                                data-testid="buttonMaps"
                                                                aria-label="Maps Button"
                                                                aria-current={
                                                                    isMapsActive
                                                                        ? 'page'
                                                                        : undefined
                                                                }
                                                                disableRipple
                                                                disableFocusRipple
                                                                disableTouchRipple
                                                                className={clsx(
                                                                    classes.iconButton,
                                                                    classes.hasDivider,
                                                                    classes.whiteFont,
                                                                    classes.reset,
                                                                    isMapsActive &&
                                                                        classes.iconButtonActive
                                                                )}
                                                            >
                                                                <MapOutlinedIcon />
                                                            </IconButton>
                                                        </NavLink>
                                                    </div>

                                                    {isStudioVisible && (
                                                        <div>
                                                            <NavLink to="/master">
                                                                <IconButton
                                                                    data-testid="buttonStudio"
                                                                    aria-label="Studio Button"
                                                                    aria-current={
                                                                        isStudioOrWorkItemPage
                                                                            ? 'page'
                                                                            : undefined
                                                                    }
                                                                    disableRipple
                                                                    disableFocusRipple
                                                                    disableTouchRipple
                                                                    className={clsx(
                                                                        classes.iconButton,
                                                                        classes.whiteFont,
                                                                        classes.hasDivider,
                                                                        classes.reset,
                                                                        isStudioOrWorkItemPage &&
                                                                            classes.iconButtonActive
                                                                    )}
                                                                >
                                                                    {!isStudioIconLoaded &&
                                                                        loader}
                                                                    <img
                                                                        className={
                                                                            classes.studioIcon
                                                                        }
                                                                        alt="studio"
                                                                        src={`${baseProjectImageUrl}/studio.svg`}
                                                                        onLoad={
                                                                            handleStudioImageLoad
                                                                        }
                                                                    />
                                                                </IconButton>
                                                            </NavLink>
                                                        </div>
                                                    )}
                                                </HiddenOnBreakpoint>
                                            ))}
                                    </div>
                                    {!pathname.includes(
                                        '/contacts/details/'
                                    ) && (
                                        <div
                                            className={clsx(
                                                isHarmonyHeader &&
                                                    classes.middleContentLight
                                            )}
                                        >
                                            <ResponsiveBreakpointContainer
                                                xs={
                                                    hamburgerOnClick
                                                        ? null
                                                        : navBarMenuToggle
                                                }
                                                sm={
                                                    !hamburgerOnClick &&
                                                    middleContent
                                                        ? navBarMenuToggle
                                                        : null
                                                }
                                                custom={
                                                    !hamburgerOnClick &&
                                                    middleContent
                                                        ? navBarMenuToggle
                                                        : null
                                                }
                                                lg={
                                                    !hamburgerOnClick &&
                                                    middleContent ? (
                                                        <GridItem
                                                            container
                                                            xs={true}
                                                            alignItems="center"
                                                            className={
                                                                classes.fullHeight
                                                            }
                                                        >
                                                            {middleContent}
                                                        </GridItem>
                                                    ) : null
                                                }
                                            />
                                        </div>
                                    )}

                                    {!pathname.includes(
                                        '/contacts/details/'
                                    ) && (
                                        <HiddenOnBreakpoint breakpoint="xs">
                                            <div
                                                className={clsx(
                                                    classes.fullHeight,
                                                    classes.flex,
                                                    isHarmonyHeader &&
                                                        classes.responsiveMenusLightOverride
                                                )}
                                            >
                                                {!hideNavigation && (
                                                    <span
                                                        className={clsx(
                                                            classes.lightBulbMain,
                                                            isHarmonyHeader &&
                                                                classes.lightBulbMainLight
                                                        )}
                                                    >
                                                        <IconButton
                                                            data-testid="trainingPageBtn"
                                                            aria-label="Training Page Button"
                                                            disableRipple
                                                            disableFocusRipple={
                                                                false
                                                            }
                                                            disableTouchRipple
                                                            onClick={() => {
                                                                navigateToExternalLink?.(
                                                                    SPECPOINT_LEARN_LINK
                                                                )
                                                            }}
                                                            className={clsx(
                                                                classes.lightBulbBtn,
                                                                isHarmonyHeader &&
                                                                    classes.lightBulbBtnLight,
                                                                isHarmonyHeader &&
                                                                    harmonyIconClasses.focusRing
                                                            )}
                                                        >
                                                            <LightBulbIcon
                                                                className={clsx(
                                                                    classes.lightBulbIcon,
                                                                    isHarmonyHeader &&
                                                                        classes.lightBulbIconLight
                                                                )}
                                                            />
                                                        </IconButton>
                                                    </span>
                                                )}

                                                {rightSideContent}
                                                {showHeaderDelaIcon && (
                                                    <DelaHeaderHOC />
                                                )}
                                            </div>
                                        </HiddenOnBreakpoint>
                                    )}
                                </GridContainer>
                            </ContainerMaxWidthXl>
                            {isHarmonyHeader && (
                                <div
                                    className={classes.accentStripLight}
                                    data-testid="appHeaderAccentStrip"
                                />
                            )}
                        </AppBar>
                    </GridItem>

                    <ResponsiveBreakpointContainer
                        xs={
                            isMenuOpen && (
                                <div
                                    id="appHeaderNavPanel"
                                    ref={menuPanelRef}
                                    className={clsx(
                                        classes.fullPageMenuContainer
                                    )}
                                >
                                    <FullNavBarMenu
                                        customContent={customMenuItems}
                                        helpMenuItems={helpMenuItems}
                                        userMenuItems={userMenuItems}
                                    />
                                </div>
                            )
                        }
                        sm={partialMenuPanel}
                        custom={partialMenuPanel}
                        lg={null}
                    />
                    {!hideActionBar && (
                        <GridItem
                            className={clsx('actionBar', classes.actionBar)}
                        >
                            {!customActionBar ? (
                                <DefaultActionBar pageTitle={pageTitle} />
                            ) : (
                                customActionBar
                            )}
                        </GridItem>
                    )}
                </>
            )}

            <Backdrop
                data-testid="smallScreenWarningBackdrop"
                open={xsDown && warningOpen}
                style={{
                    zIndex: theme.zIndex.appBar,
                    backgroundColor: 'rgba(0,0,0,0.85)',
                }}
            >
                <IconButton
                    data-testid="smallScreenWarningCloseButton"
                    className={clsx(
                        classes.whiteFont,
                        classes.warningCloseButton
                    )}
                    onClick={closeWarningOverlay}
                >
                    <CloseIcon fontSize="large" />
                </IconButton>
                <Typography
                    variant="body2"
                    className={classes.smallScreenWarningText}
                >
                    This website is optimized for tablet and desktop.
                    <br />
                    Please view on a bigger screen.
                </Typography>
            </Backdrop>
        </GridContainer>
    )
}

export default AppHeader
